import { ConfirmDialog } from "@/components/ConfirmDialog";
import { OneDriveIcon, OutlookCalendarIcon, OutlookIcon } from "@/components/MicrosoftIcons";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { TeamsIcon } from "@/components/TeamsIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, type ContainerRow, type Ms365Status } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Check, Loader2, Unplug } from "lucide-react";
import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { toast } from "sonner";
import {
  choicesDiffer,
  grantedChoices,
  MS365_CAPABILITIES,
  NO_SCOPES,
  parseSavedChoices,
  sameChoice,
  scopesFor,
  type Choices,
  type Ms365CapabilityId,
} from "@/lib/ms365-access";
import { CapabilityList, CapabilityRow } from "./CapabilityRows";
import { ConnectionHeader, ConnectionSection, StatusDot } from "./ConnectionParts";
import { ms365StatusKey, type ConnectionDef } from "./connections-lib";

// Microsoft 365 connection page. OAuth is centralized in boo-backend (one app, one
// redirect URI, one secret): an admin grants consent, then the dedicated boo@
// account signs in. The customer picks which capabilities to grant and
// re-authorises to change them; the selected Graph scopes ride both steps as a
// `scopes` param.
//
// Permissions come from the /status response's `_permissions`, not the
// container list: that object doesn't carry the ms365 gates.

const CAPABILITY_ICONS: Record<Ms365CapabilityId, ComponentType<{ className?: string }>> = {
  files: OneDriveIcon,
  teams: TeamsIcon,
  mail: OutlookIcon,
  calendar: OutlookCalendarIcon,
};

// The picks and account survive the round trip to Microsoft in sessionStorage:
// the page reloads on return, and the admin-consent step comes back before the
// sign-in step that needs them.
const draftKey = (containerId: string) => `boo.ms365.draft.${containerId}`;

function readSavedDraft(containerId: string): { choices: Choices; account: string } | null {
  try {
    const raw = sessionStorage.getItem(draftKey(containerId));
    const saved = raw ? (JSON.parse(raw) as { account?: unknown }) : null;
    const choices = parseSavedChoices(saved);
    if (!choices) return null;
    return { choices, account: typeof saved?.account === "string" ? saved.account : "" };
  } catch {
    return null;
  }
}

// What the Microsoft callback brought back (?ms365=<outcome>), with the picks
// saved before leaving unless the connection now holds them.
function readReturn(containerId: string): {
  outcome: string | null;
  draft: Choices | null;
  account: string | null;
} {
  const outcome = new URL(window.location.href).searchParams.get("ms365");
  const saved = outcome && outcome !== "connected" ? readSavedDraft(containerId) : null;
  return { outcome, draft: saved?.choices ?? null, account: saved?.account ?? null };
}

export function Microsoft365Settings({
  entry,
  container,
}: {
  entry: ConnectionDef;
  container: ContainerRow;
}) {
  const queryClient = useQueryClient();
  const queryKey = ms365StatusKey(container.id);
  const statusQuery = useQuery({
    queryKey,
    queryFn: () => api.ms365.status(container.id),
  });
  const status = statusQuery.data;

  const [returned] = useState(() => readReturn(container.id));
  const granted = status?.grantedScopes ?? NO_SCOPES;
  const baseline = useMemo(() => grantedChoices(granted), [granted]);
  // null = follow what's granted; set once the viewer changes something.
  const [draft, setDraft] = useState<Choices | null>(returned.draft);
  const choices = draft ?? baseline;
  const [accountDraft, setAccountDraft] = useState<string | null>(returned.account);
  const account = accountDraft ?? status?.account ?? "";
  // Admin consent came back on this visit; the backend doesn't record it.
  const consented = returned.outcome === "admin_consented";
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  // The callback appends ?ms365=<outcome>, so this path must stay query-free.
  const returnTo = `/orgs/${container.organizationId}/containers/${container.id}/connections/${entry.id}`;

  useEffect(() => {
    const url = new URL(window.location.href);
    const outcome = url.searchParams.get("ms365");
    if (!outcome) return;
    if (outcome === "connected") {
      toast.success("Microsoft 365 connected");
    } else if (outcome === "admin_consented") {
      toast.success("Access approved. Now sign in as the Boo account.");
    } else if (outcome === "wrong_account") {
      toast.error("That sign-in didn't match the account you entered. Sign in again as that account.");
    } else {
      toast.error("Couldn't connect Microsoft 365. Please try again.");
    }
    try {
      sessionStorage.removeItem(draftKey(container.id));
    } catch {
      // No storage: nothing was saved.
    }
    queryClient.invalidateQueries({ queryKey: ms365StatusKey(container.id) });
    queryClient.invalidateQueries({ queryKey: ["containers"] });
    url.searchParams.delete("ms365");
    window.history.replaceState(window.history.state, "", url.pathname + url.search);
  }, [queryClient, container.id]);

  const scopesParam = scopesFor(choices).join(" ");
  const goToMicrosoft = ({ url }: { url: string }) => {
    try {
      sessionStorage.setItem(draftKey(container.id), JSON.stringify({ choices, account }));
    } catch {
      // No storage: the picks are chosen again on return.
    }
    window.location.href = url;
  };
  const adminConsent = useMutation({
    mutationFn: () => api.ms365.adminConsent(container.id, scopesParam, returnTo),
    onSuccess: goToMicrosoft,
  });
  const signIn = useMutation({
    mutationFn: () => api.ms365.install(container.id, account.trim(), scopesParam, returnTo),
    onSuccess: goToMicrosoft,
  });
  const disconnect = useMutation({
    mutationFn: () => api.ms365.disconnect(container.id),
    onSuccess: () => {
      queryClient.setQueryData<Ms365Status>(queryKey, (prev) =>
        prev ? { ...prev, connected: false, account: null, grantedScopes: [] } : prev,
      );
      queryClient.invalidateQueries({ queryKey });
      setDisconnectOpen(false);
      setDraft(null);
      setAccountDraft(null);
      toast.success("Microsoft 365 disconnected");
    },
  });

  const connected = status?.connected === true;
  const canConnect = status?._permissions?.canConnect !== false;
  const canDisconnect = status?._permissions?.canDisconnect !== false;
  const anyOn = MS365_CAPABILITIES.some((c) => choices[c.id]!.on);
  const dirty = connected && choicesDiffer(choices, baseline);

  const header = (
    <ConnectionHeader
      def={entry}
      status={
        !status ? (
          "Checking…"
        ) : connected ? (
          <>
            <StatusDot state="connected" />
            <span className="truncate">
              Connected as <span className="font-medium text-foreground">{status.account}</span>
            </span>
          </>
        ) : (
          <>
            <StatusDot state="off" />
            Not connected
          </>
        )
      }
      actions={
        connected &&
        canDisconnect && (
          <Button
            size="sm"
            variant="ghost"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={() => setDisconnectOpen(true)}
          >
            <Unplug className="size-3.5" />
            Disconnect
          </Button>
        )
      }
    />
  );

  if (statusQuery.isLoading) {
    return (
      <>
        {header}
        <LoadingIndicator message="Checking Microsoft 365…" />
      </>
    );
  }
  if (statusQuery.isError || !status) {
    return (
      <>
        {header}
        <InlineError error={statusQuery.error ?? "Failed to check Microsoft 365 status"} />
      </>
    );
  }

  return (
    <>
      {header}

      {!connected && (
        <p className="max-w-2xl text-sm text-muted-foreground">
          Boo signs in to Microsoft 365 with an account of its own, the way a new teammate would.
          Choose what it can use, have an admin approve that for your organization, then sign in as
          the Boo account.
        </p>
      )}

      {!canConnect && !connected ? (
        <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          Ask one of this Boo's admins to connect Microsoft 365.
        </div>
      ) : (
        <ConnectionSection
          title="What Boo can use"
          description={
            !canConnect
              ? "Only this Boo's admins can change this."
              : connected
                ? "Changes apply once you approve them and sign in again."
                : "Turn on what Boo needs. You can change this later."
          }
        >
          <CapabilityList>
            {MS365_CAPABILITIES.map((cap) => {
              const Icon = CAPABILITY_ICONS[cap.id];
              return (
                <CapabilityRow
                  key={cap.id}
                  idPrefix="ms365"
                  cap={cap}
                  icon={<Icon className="size-6" />}
                  choice={choices[cap.id]!}
                  pending={connected && !sameChoice(choices[cap.id]!, baseline[cap.id]!)}
                  editable={canConnect}
                  onChange={(next) => setDraft({ ...choices, [cap.id]: next })}
                />
              );
            })}
          </CapabilityList>
          {dirty && (
            <Button size="sm" variant="ghost" className="-ml-2 text-muted-foreground" onClick={() => setDraft(null)}>
              Undo changes
            </Button>
          )}
        </ConnectionSection>
      )}

      {canConnect && (!connected || dirty) && (
        <ConnectionSection
          title={connected ? "Apply your changes" : "Connect"}
          description={
            anyOn
              ? undefined
              : connected
                ? "To remove all access, disconnect instead."
                : "Turn on at least one of the above to continue."
          }
        >
          <ol className="divide-y rounded-lg border">
            <Step
              n={1}
              done={consented}
              title="Approve access for your organization"
              description="A Microsoft 365 admin approves the access above, once for your organization and again whenever you add access. Starting it takes an owner or admin of your Boo workspace."
            >
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                disabled={!anyOn || adminConsent.isPending}
                onClick={() => adminConsent.mutate()}
              >
                {adminConsent.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <ArrowUpRight className="size-3.5" />
                )}
                {consented ? "Approve again" : "Approve in Microsoft"}
              </Button>
              {adminConsent.isError && <InlineError error={adminConsent.error} />}
            </Step>
            <Step
              n={2}
              title="Sign in as the Boo account"
              description="Use the Microsoft account Boo works as, such as boo@yourcompany.com, not your own."
            >
              <form
                className="flex flex-col gap-2 sm:flex-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  signIn.mutate();
                }}
              >
                <Input
                  type="email"
                  aria-label="Boo account"
                  placeholder="boo@yourcompany.com"
                  value={account}
                  onChange={(e) => setAccountDraft(e.target.value)}
                  className="sm:max-w-xs"
                />
                <Button
                  type="submit"
                  className="gap-1.5"
                  disabled={!account.trim() || !anyOn || signIn.isPending}
                >
                  {signIn.isPending && <Loader2 className="size-3.5 animate-spin" />}
                  {connected ? "Sign in again" : "Sign in with Microsoft"}
                </Button>
              </form>
              {signIn.isError && <InlineError error={signIn.error} />}
            </Step>
          </ol>
        </ConnectionSection>
      )}

      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title="Disconnect Microsoft 365?"
        description={`Boo stops using files, Teams history, mail, and calendar through ${status.account ?? "this account"}. You can connect again anytime.`}
        confirmLabel="Disconnect"
        loading={disconnect.isPending}
        onConfirm={() => disconnect.mutate()}
      />
      {disconnect.isError && <InlineError error={disconnect.error} />}
    </>
  );
}

function Step({
  n,
  done = false,
  title,
  description,
  children,
}: {
  n: number;
  done?: boolean;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-3 p-4">
      <span
        className={cn(
          "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium tabular-nums",
          done ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground ring-1 ring-border ring-inset",
        )}
        aria-label={done ? `Step ${n}, done` : `Step ${n}`}
      >
        {done ? <Check className="size-3.5" strokeWidth={3} /> : n}
      </span>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="space-y-0.5 pt-0.5">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        {children}
      </div>
    </li>
  );
}
